import { useCallback, useEffect, useMemo } from 'react';

import useCommonStore from '@/stores/pure/CommonStore';

import useStates from './useStates';

type State<T extends {} | null> = {
  data: T | null;
  loading: boolean;
  fetched: boolean;
  error: boolean;
};

const useSingleData = <T extends {} | null>(
  callback: () => Promise<T>,
  enable?: boolean,
): State<T> & {
  refetch: () => void;
} => {
  const appInit = useCommonStore((state) => state.appInit);
  const defaultEnable = useMemo(
    () => (typeof enable === 'undefined' ? true : enable),
    [enable],
  );

  const { state, setState } = useStates<State<T>>({
    data: null,
    loading: false,
    fetched: false,
    error: false,
  });

  const fetchData = useCallback(async () => {
    try {
      setState((prev) => ({
        ...prev,
        loading: true,
        fetched: false,
        error: false,
      }));
      const res = await callback();
      setState((prev) => ({
        ...prev,
        data: res,
        loading: false,
        fetched: true,
        error: false,
      }));
    } catch (e) {
      setState((prev) => ({
        ...prev,
        loading: false,
        fetched: true,
        error: true,
      }));
    }
  }, [callback]);

  useEffect(() => {
    if (!appInit || !defaultEnable) return;
    fetchData();
  }, [appInit, fetchData, defaultEnable]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    ...state,
    refetch,
  };
};

export default useSingleData;
